import { User } from "../context/AuthContext"
import { Device, DevicesAction } from "../context/DeviceContext"
import { System, SystemDevice } from "../context/SystemContext"
import { getBackendUrl } from "../env"

export const getDevicesWithoutSystem = async (user:User) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/withoutSystem`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`
      }
    })
    const json = await response.json()
    if (response.ok) {
      return json
    } 
}

export const getDevicesInSystem = async (user:User, id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/inSystem/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`
      }
    })
    const json = await response.json()
    if (response.ok) {
      return json
    } 
}

export const updateDevice = async (user:User, outlet:Device) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/`+outlet._id, {
    method: 'PATCH',
    body: JSON.stringify(outlet),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if(response.ok){
    return await json
  }
  else{
    return "Error"
  }
}
// The second parameter is the device._id NOT device.device_id
export const removeSystemFromDevice = async (user:User, device_id:string, json:any) => {
  const response2 = await fetch(`${getBackendUrl()}/api/devices/`+ device_id, {
    method: 'PATCH',
    body: JSON.stringify({system_id: ''}),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  if(response2.ok){
    console.log("removed Device")
  }
  return {devices: json.devices, system:json}
}


/** Matches `variable` on each item in `sensor_groups` (array shape from API / SystemSettings). */
const SENSOR_VARIABLE_BY_DEVICE_TYPE: Record<string, string> = {
  'Temperature Sensor': 'Temperature',
  'Humidity Sensor': 'Humidity',
  'EC Sensor': 'EC',
  'PH Sensor': 'pH',
  'Moisture Sensor': 'Moisture',
  'TDS Sensor': 'TDS',
  'Water Flow Rate Sensor': 'Water Flow Rate',
}

const removeSensorFromSystemSensorGroups = async (system: any, device: SystemDevice) => {
  if (!(system instanceof Object) || !('sensor_groups' in system) || !system.sensor_groups) {
    return system
  }
  const variable = SENSOR_VARIABLE_BY_DEVICE_TYPE[device.device_type]
  if (!variable) {
    if (device.device_type.includes('Sensor')) {
      console.warn(`Unknown sensor type for removal: ${device.device_type}`)
    }
    return system
  }

  if (Array.isArray(system.sensor_groups)) {
    system.sensor_groups = system.sensor_groups.map((g: { variable?: string; sensors?: { device_id: string }[] }) => {
      if (g?.variable !== variable) return g
      return {
        ...g,
        sensors: (g.sensors ?? []).filter((obj) => obj.device_id !== device.device_id),
      }
    })
    return system
  }

  // Legacy: sensor_groups keyed by variable name with a `.sensors` array
  const group = (system.sensor_groups as Record<string, { sensors?: { device_id: string }[] }>)[variable]
  if (group && Array.isArray(group.sensors)) {
    group.sensors = group.sensors.filter((obj) => obj.device_id !== device.device_id)
  }
  return system
}

const CONTROLLER_GROUP_VARIABLES_BY_DEVICE_TYPE: Record<string, string[]> = {
  'Outlet ESO15-TB 2': ['Temperature', 'Humidity'],
  'Pump Device 4 Mixer': ['EC', 'pH'],
}

const removeControllerFromSystemSensorGroups = async (system: any, device: SystemDevice) => {
  const variables = CONTROLLER_GROUP_VARIABLES_BY_DEVICE_TYPE[device.device_type]
  if (!variables || !system.sensor_groups) {
    return system
  }

  if (Array.isArray(system.sensor_groups)) {
    system.sensor_groups = system.sensor_groups.map(
      (g: {
        variable?: string
        controllers?: { device_id: string }[]
        controllers_info?: { device_id: string }[]
      }) => {
        if (!variables.includes(g?.variable ?? '')) return g
        return {
          ...g,
          controllers: (g.controllers ?? []).filter((obj) => obj.device_id !== device.device_id),
          controllers_info: (g.controllers_info ?? []).filter((obj) => obj.device_id !== device.device_id),
        }
      }
    )
    return system
  }

  for (const v of variables) {
    const grp = (system.sensor_groups as Record<string, { controllers?: { device_id: string }[]; controllers_info?: { device_id: string }[] }>)[v]
    if (!grp) continue
    grp.controllers = (grp.controllers ?? []).filter((obj) => obj.device_id !== device.device_id)
    grp.controllers_info = (grp.controllers_info ?? []).filter((obj) => obj.device_id !== device.device_id)
  }
  return system
}


export const removeDeviceFromSystem = async (user: User, system: any, device: SystemDevice) => {
  if (!(system instanceof Object) || !('_id' in system)) {
    return JSON.stringify({ error: 'No system found.' })
  }
  if (!Array.isArray(system.devices)) {
    system.devices = []
  }
  const index = system.devices.findIndex((obj: { device_id: string }) => obj.device_id === device.device_id)
  if (index > -1) {
    system.devices.splice(index, 1)
  }
  if (device.device_type.includes('Sensor')) {
    system = await removeSensorFromSystemSensorGroups(system, device)
  } else {
    system = await removeControllerFromSystemSensorGroups(system, device)
  }
  const response = await fetch(`${getBackendUrl()}/api/systems/` + system._id, {
    method: 'PATCH',
    body: JSON.stringify({ devices: system.devices, sensor_groups: system.sensor_groups }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user.token}`,
    },
  })
  return response
}

export const fetchDevices = async (user: User, dispatch: (action: DevicesAction) => void) => {
    const response = await fetch(`${getBackendUrl()}/api/devices`, {
      headers: {'Authorization': `Bearer ${user.token}`},
    })
    const json = await response.json()
    console.log("fetchDevices", json)
    if (response.ok) {
      dispatch({type: 'SET_DEVICES', payload: json})
    }
  }

export const getDeviceReading = async (user:User, device_id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/reading/`+device_id, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if (response.ok){
    let reading = Number(json.value).toFixed(4)
    let timestamp = await json.timestamp
    return {reading:reading, timestamp:timestamp}
  }
  return {reading:'--', timestamp: '--'}
}

export const getDeviceReadings = async (user:User, device_id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/readings/`+device_id, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })

  const json = await response.json()
  console.log("getDeviceReadings", json)
  if (response.ok){
    return json
  }
  if (!response.ok){
    return {reading:json}
  }
}

export const getDeviceReadingsPastHour = async (user:User, device_id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/readingsHour/`+device_id, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if (response.ok){
    return json
  }
  if (!response.ok){
    return {reading:json}
  }
}



export const getDeviceReadingsPastDay = async (user:User, device_id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/readingsDay/`+device_id, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if (response.ok){
    return json
  }
  if (!response.ok){
    return {reading:json}
  }
}

export const getDeviceReadingsPastWeek = async (user:User, device_id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/readingsWeek/`+device_id, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if (response.ok){
    return json
  }
  if (!response.ok){
    return {reading:json}
  }
}

export const getDeviceReadingsPastMonth = async (user:User, device_id:string) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/readingsMonth/`+device_id, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if (response.ok){
    return json
  }
  if (!response.ok){
    return {reading:json}
  }
}

export type SparklineReadingsResponse = {
  values: number[]
  timestamps: string[]
}

/** Recent raw readings for sparklines (newest `limit` samples within `hours`). */
export const getDeviceReadingsSparkline = async (
  user: User,
  device_id: string,
  opts?: { limit?: number; hours?: number }
): Promise<SparklineReadingsResponse | { reading: unknown }> => {
  const params = new URLSearchParams()
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.hours != null) params.set('hours', String(opts.hours))
  const q = params.toString()
  const url =
    `${getBackendUrl()}/api/devices/readingsSparkline/${device_id}` + (q ? `?${q}` : '')
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user.token}`,
    },
  })
  const json = await response.json()
  if (response.ok) {
    return json as SparklineReadingsResponse
  }
  return { reading: json }
}

/** PATCH a single device's system assignment (Mongo device document id). */
export const patchDeviceSystemId = async (
  user: User,
  deviceMongoId: string,
  systemId: string
): Promise<boolean> => {
  const response = await fetch(`${getBackendUrl()}/api/devices/` + deviceMongoId, {
    method: 'PATCH',
    body: JSON.stringify({ system_id: systemId }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user.token}`,
    },
  })
  return response.ok
}

/**
 * Assign `systemId` to every device in `nextDevicesInSystem`, and clear `system_id` for any device
 * that was previously in this system but is missing from the list. Devices are the source of truth
 * (system documents no longer embed a devices array).
 */
export const syncDevicesWithSystem = async (
  user: User,
  systemMongoId: string,
  nextDevicesInSystem: { _id: string }[]
): Promise<boolean> => {
  const prev = await getDevicesInSystem(user, systemMongoId)
  const prevList = Array.isArray(prev) ? prev : []
  const nextIds = new Set(nextDevicesInSystem.map((d) => String(d._id)))
  const removed = prevList.filter((d: { _id: string }) => !nextIds.has(String(d._id)))

  const results: boolean[] = []
  for (const d of nextDevicesInSystem) {
    results.push(await patchDeviceSystemId(user, String(d._id), systemMongoId))
  }
  for (const d of removed) {
    results.push(await patchDeviceSystemId(user, String((d as { _id: string })._id), ''))
  }
  return results.length === 0 || results.every(Boolean)
}

/** @deprecated Use syncDevicesWithSystem — this name is kept for any stray imports. */
export const updateDeviceSystem = syncDevicesWithSystem

export const updateDeviceInfo = async (user:User, device:Device) => {
  const response = await fetch(`${getBackendUrl()}/api/devices/`+device._id, {
    method: 'PATCH',
    body: JSON.stringify(device),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${user.token}`
    }
  })
  const json = await response.json()
  if (response.ok) {
    console.log("device updated")
    return json
  }
  if (!response.ok) {
    console.log("error not updated")
    return 
  }
}